<script lang="ts" setup>
import EventLogApi from '@zyro-inc/site-modules/api/EventLogApi';
import EcommerceShoppingCart from '@zyro-inc/site-modules/components/ecommerce/EcommerceShoppingCart.vue';
import { getIsInIframe } from '@zyro-inc/site-modules/utils/getIsInIframe';
import { isAppPrerendering } from '@zyro-inc/site-modules/utils/prerenderingFlags';
import { useEcommerceModal } from '@zyro-inc/site-modules/components/ecommerce/modals/useEcommerceModal';
import { useEcommerce } from '@zyro-inc/site-modules/components/ecommerce/useEcommerce';
import { useEcommerceGlobal } from '@zyro-inc/site-modules/use/useEcommerceGlobal';
import { useSiteGlobal } from '@zyro-inc/site-modules/use/useSiteGlobal';
import {
	onMounted,
	watch,
	ref,
} from 'vue';
import { getStoreId } from '@zyro-inc/site-modules/utils/getters/getStoreId';
import {
	EcommerceProduct,
	EcommerceCart,
} from '@zyro-inc/site-modules/types';

type Props = {
	stickyBarHeight?: number,
	ecommerceTranslations: Record<string, string>,
	language: string,
	isHeaderSticky?: boolean,
	isHeaderHidden?: boolean,
	isInPreviewMode?: boolean
	isMobile: boolean
	isExpressCheckoutEnabled: boolean
}

const props = withDefaults(defineProps<Props>(), {
	stickyBarHeight: 0,
	isHeaderSticky: false,
	isHeaderHidden: false,
	isInPreviewMode: false,
	isExpressCheckoutEnabled: false,
});

const { siteId } = useSiteGlobal();
const {
	quantifiedCartItemsList,
	isShoppingCartOpen,
	isCheckoutLoading,
	shoppingCartItems,
	variantsQuantity,
	fetchCartProducts,
	setShoppingCartOpen,
	setShoppingCartItems,
	setIsCheckoutLoading,
	updateCartData,
	initiateExpressCart,
	createExpressCart,
	stripePublicKey,
	loadCartProductSuggestions,
	regions,
	isCartProductSuggestionsEnabled,
	cartProductSuggestions,
	hasOutdatedCartItems,
	resetHasOutdatedCartItems,
} = useEcommerceGlobal();
const { openEcommerceModal } = useEcommerceModal();
const { initiateCheckout } = useEcommerce();

const expressCartData = ref({} as EcommerceCart);
const isAmountAdjusted = ref(false);
const isItemDeleted = ref(false);

const loadProducts = async () => {
	if (!isAppPrerendering) {
		await fetchCartProducts();
	}

	if (hasOutdatedCartItems.value) {
		resetHasOutdatedCartItems();
		openEcommerceModal('EcommerceOutdatedCartItem');
	}
};

const initCheckout = async () => {
	EventLogApi.logEvent({
		eventName: 'builder_customers.ecomm_checkout.enter',
		eventProperties: {
			product_type: [...new Set(shoppingCartItems.value.map((item) => item.type.value))],
			express_checkout: props.isExpressCheckoutEnabled,
			suggested_products: cartProductSuggestions.value.length > 0,
			was_amount_adjusted: isAmountAdjusted.value,
			was_item_deleted: isItemDeleted.value,
		},
	});

	if (getIsInIframe() || props.isInPreviewMode) {
		openEcommerceModal('EcommerceMessageButtonDisabled');

		return;
	}

	if (isCheckoutLoading.value) {
		return;
	}

	EventLogApi.logEvent({
		eventName: 'builder_customers.ecomm_checkout.enter',
		eventProperties: {
			product_type: [...new Set(shoppingCartItems.value.map((item) => item.type.value))],
			express_checkout: props.isExpressCheckoutEnabled,
			suggested_products: cartProductSuggestions.value.length > 0,
			was_amount_adjusted: isAmountAdjusted.value,
			was_item_deleted: isItemDeleted.value,

		},
	});

	setIsCheckoutLoading(true);

	await initiateCheckout(shoppingCartItems.value).then(() => {
		setShoppingCartOpen(false);
		setIsCheckoutLoading(false);

		isAmountAdjusted.value = false;
		isItemDeleted.value = false;
	});
};

const handleCreateCart = (productData?: EcommerceProduct) => {
	const data = productData ? [productData] : [];

	createExpressCart(data);
};

const initCart = async () => {
	expressCartData.value = await initiateExpressCart() as EcommerceCart;
};

watch(() => props.isExpressCheckoutEnabled, (isEnabled) => {
	if (isEnabled) {
		initCart();
	}
});

watch(isShoppingCartOpen, () => {
	if (isCartProductSuggestionsEnabled.value && !!shoppingCartItems.value?.length) {
		loadCartProductSuggestions();
	}
});

onMounted(async () => {
	await loadProducts();
});
</script>

<template>
	<EcommerceShoppingCart
		v-qa="'user-section-zyroecommerceshoppingcart'"
		:is-shopping-cart-open="isShoppingCartOpen"
		:is-loading="isCheckoutLoading"
		:sticky-bar-height="stickyBarHeight"
		:is-header-sticky="isHeaderSticky"
		:is-header-hidden="isHeaderHidden"
		:translations="ecommerceTranslations"
		:language="language"
		:site-id="siteId"
		:quantified-cart-items-list="quantifiedCartItemsList"
		:shopping-cart-items="shoppingCartItems"
		:variants-quantity="variantsQuantity"
		:is-mobile="isMobile"
		:is-express-checkout-enabled="isExpressCheckoutEnabled"
		:store-id="getStoreId()"
		:update-cart-data="updateCartData"
		:express-cart-data="expressCartData"
		:regions="regions"
		:stripe-public-key="stripePublicKey"
		:is-preview-mode="false"
		:cart-product-suggestions="cartProductSuggestions"
		:is-in-preview-mode="isInPreviewMode"
		@checkout-button-click="initCheckout"
		@close-shopping-cart="setShoppingCartOpen(false)"
		@set-shopping-cart-items="setShoppingCartItems"
		@create-express-cart="handleCreateCart"
		@amount-adjusted="isAmountAdjusted = true"
		@item-deleted="isItemDeleted = true"
	/>
</template>
